import {
  getEnvHosts,
  resolveRuntimeEnv,
  shouldUseLocalProjectPages,
} from "./runtimeEnv";

const LOCAL_PROJECT_PORTS = {
  clientH5: 8080,
  clientWeb: 8082,
  clientReport: 9002,
};

const CLIENT_WEB_HOSTS = new Set([
  "webclient.sscanmou.com",
  "huawei-test-webclient.sscanmou.com",
]);

const CLIENT_REPORT_HOSTS = new Set([
  "admin.sscanmou.com",
  "huawei-test-admin.sscanmou.com",
  "client-report.sscanmou.com",
  "client-report-test.sscanmou.com",
]);

export const useLocalProjectPages = shouldUseLocalProjectPages();

const getLocalHostname = () => window.location.hostname || "localhost";

export const getLocalProjectOrigin = project => {
  return `${window.location.protocol}//${getLocalHostname()}:${LOCAL_PROJECT_PORTS[project]}`;
};

export const isAbsoluteWebUrl = url => /^https?:\/\//i.test(url);

const replaceOrigin = (url, origin) => `${origin}${url.pathname}${url.search}${url.hash}`;

const toAbsoluteUrl = value => {
  if (value.startsWith("//")) return `${window.location.protocol}${value}`;
  return value;
};

/** 按当前业务环境，把测试/生产域名改写到配置环境 */
const rewriteToConfiguredEnv = rawUrl => {
  const env = resolveRuntimeEnv();
  const hosts = getEnvHosts(env);
  const value = rawUrl.trim();

  // 相对 H5 / report 路径：补成配置环境域名
  if (/^\/?h5\/#/i.test(value)) {
    const path = value.startsWith("/") ? value : `/${value}`;
    return `${hosts.webclient}${path}`;
  }
  if (/^\/?report-xx(?:\/|$)/i.test(value)) {
    const path = value.replace(/^\/?report-xx/i, "") || "/";
    const reportBase = hosts.report.replace(/\/$/, "");
    return `${reportBase}${path.startsWith("/") ? path : `/${path}`}`;
  }

  if (!/^(https?:)?\/\//i.test(value)) return rawUrl;

  try {
    const url = new URL(toAbsoluteUrl(value));
    const hostname = url.hostname.toLowerCase();

    if (CLIENT_WEB_HOSTS.has(hostname)) {
      const target = new URL(hosts.webclient);
      url.protocol = target.protocol;
      url.hostname = target.hostname;
      url.port = target.port;
      return url.toString();
    }

    if (CLIENT_REPORT_HOSTS.has(hostname) && /^\/report-xx(?:\/|$)/i.test(url.pathname)) {
      const reportAbs = toAbsoluteUrl(hosts.report);
      const target = new URL(reportAbs.includes("://") ? reportAbs : `https:${reportAbs}`);
      // hosts.report 已包含 /report-xx 部署前缀。
      const reportPathPrefix = target.pathname.replace(/\/$/, "") || "/report-xx";
      const rest = url.pathname.replace(/^\/report-xx(?=\/|$)/i, "") || "";
      url.protocol = target.protocol;
      url.hostname = target.hostname;
      url.port = target.port;
      url.pathname = `${reportPathPrefix}${rest}` || reportPathPrefix;
      return url.toString();
    }
  } catch (error) {
    return rawUrl;
  }

  return rawUrl;
};

/**
 * 本地开发把三个前端项目之间的跳转固定到当前局域网主机：
 * client-H5: 8080、client-web: 8082、client-report: 9002。
 * 业务接口环境仍由 runtimeEnv.RUNTIME_ENV 决定（可本地对接生产）。
 */
export const resolveProjectNavigationUrl = rawUrl => {
  if (typeof rawUrl !== "string") return rawUrl;

  // 非本地联调：嵌套地址跟随 RUNTIME_ENV 配置域名
  if (!useLocalProjectPages) {
    return rewriteToConfiguredEnv(rawUrl);
  }

  const value = rawUrl.trim();

  if (/^\/?h5\/#/i.test(value)) {
    const path = value.startsWith("/") ? value : `/${value}`;
    return `${getLocalProjectOrigin("clientH5")}${path}`;
  }

  if (/^\/?report-xx(?:\/|$)/i.test(value)) {
    const path = value.replace(/^\/?report-xx/i, "") || "/";
    return `${getLocalProjectOrigin("clientReport")}${path.startsWith("/") ? path : `/${path}`}`;
  }

  if (!/^(https?:)?\/\//i.test(value)) return rawUrl;

  try {
    const url = new URL(value.startsWith("//") ? `${window.location.protocol}${value}` : value);
    const hostname = url.hostname.toLowerCase();

    if (CLIENT_WEB_HOSTS.has(hostname)) {
      const project = /^\/h5(?:\/|$)/i.test(url.pathname) ? "clientH5" : "clientWeb";
      return replaceOrigin(url, getLocalProjectOrigin(project));
    }

    if (CLIENT_REPORT_HOSTS.has(hostname) && /^\/report-xx(?:\/|$)/i.test(url.pathname)) {
      url.pathname = url.pathname.replace(/^\/report-xx(?=\/|$)/i, "") || "/";
      return replaceOrigin(url, getLocalProjectOrigin("clientReport"));
    }
  } catch (error) {
    return rawUrl;
  }

  return rawUrl;
};
